import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Divider, Grid, TextField, Typography, InputAdornment, IconButton, Paper,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { PageHeader } from '../components/ui/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import { catchyPrimaryButtonSx, catchyTextFieldSx } from '../theme/formStyles';
import { brandColors, TYPE, APP_FONT_FAMILY } from '../theme/appTheme';
import { useAppTheme } from '../contexts/ThemeContext';
import type { User } from '../types';

const apiErrorMessage = (err: unknown, fallback: string) => {
  const ax = err as {
    response?: { status?: number; data?: { message?: string } };
    message?: string;
    code?: string;
  };
  if (ax.response?.data?.message) return ax.response.data.message;
  if (ax.response?.status === 404) {
    return 'Profile API not found. Restart the backend server and try again.';
  }
  if (ax.response?.status === 401) {
    return 'Session expired. Please sign in again.';
  }
  if (ax.code === 'ERR_NETWORK' || ax.message === 'Network Error') {
    return 'Cannot reach the server. Check that the backend is running.';
  }
  return fallback;
};

export const ProfilePage: React.FC = () => {
  const { user, applyUser, refreshUser } = useAuth();
  const { brand } = useAppTheme();
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [designation, setDesignation] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) return;
    setName(user.name || '');
    setMobile(user.mobile || '');
    setDesignation(user.designation || '');
  }, [user]);

  const handleSave = async () => {
    setError('');
    setMessage('');
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (newPassword || confirmPassword || currentPassword) {
      if (!currentPassword) {
        setError('Enter your current password to change it');
        return;
      }
      if (newPassword.length < 6) {
        setError('New password must be at least 6 characters');
        return;
      }
      if (newPassword !== confirmPassword) {
        setError('New password and confirmation do not match');
        return;
      }
    }

    try {
      setLoading(true);
      const payload: Record<string, string> = {
        name: name.trim(),
        mobile: mobile.trim(),
        designation: designation.trim(),
      };
      if (newPassword) {
        payload.currentPassword = currentPassword;
        payload.newPassword = newPassword;
      }
      const res = await authAPI.updateProfile(payload);
      const nextUser = res.data?.user as User | undefined;
      if (nextUser) applyUser(nextUser);
      else await refreshUser();
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setMessage(res.data?.message || (newPassword ? 'Profile and password updated.' : 'Profile updated successfully.'));
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to update profile'));
    } finally {
      setLoading(false);
    }
  };

  const roleLabel =
    user?.roleType === 'super_admin'
      ? 'Super Admin'
      : user?.roleType === 'tenant_admin'
        ? 'Company Admin'
        : 'Staff';

  return (
    <Box sx={{ width: '100%', maxWidth: 880, mx: 'auto' }}>
      <PageHeader
        title="My Profile"
        subtitle="Update your personal details. Login email cannot be changed."
        icon={<PersonOutlineIcon />}
      />

      {message && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          borderRadius: '16px',
          border: `1px solid ${brandColors.border}`,
          overflow: 'hidden',
          boxShadow: '0 4px 24px rgba(15,23,42,0.05)',
        }}
      >
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            background: `linear-gradient(135deg, ${brand.green50} 0%, #fff 70%)`,
            borderBottom: `1px solid ${brandColors.border}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: '14px',
              background: brand.gradientPrimary,
              color: '#fff',
              display: 'grid',
              placeItems: 'center',
              fontWeight: 700,
              fontSize: 18,
              fontFamily: APP_FONT_FAMILY,
            }}
          >
            {(user?.name || 'U').slice(0, 1).toUpperCase()}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: TYPE.cardHeading.fontSize, color: brandColors.textDark }}>
              {user?.name || 'User'}
            </Typography>
            <Typography sx={{ fontSize: TYPE.secondary.fontSize, color: brandColors.textMuted }}>
              {user?.email} · {roleLabel}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography fontWeight={600} fontSize={15} color={brandColors.textDark} mb={2}>
            Personal information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Login email"
                value={user?.email || ''}
                fullWidth
                disabled
                sx={catchyTextFieldSx}
                helperText="Login email is locked for security"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                fullWidth
                sx={catchyTextFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Mobile"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                fullWidth
                sx={catchyTextFieldSx}
                placeholder="10-digit mobile"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Designation"
                value={designation}
                onChange={(e) => setDesignation(e.target.value)}
                fullWidth
                sx={catchyTextFieldSx}
                placeholder="e.g. Fleet Manager"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Role"
                value={roleLabel}
                fullWidth
                disabled
                sx={catchyTextFieldSx}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <LockOutlinedIcon sx={{ fontSize: 18, color: brand.primary }} />
            <Typography fontWeight={600} fontSize={15} color={brandColors.textDark}>
              Change password
            </Typography>
          </Box>
          <Typography fontSize={13} color={brandColors.textMuted} mb={2}>
            Leave blank if you do not want to change your password.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Current password"
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                fullWidth
                autoComplete="current-password"
                sx={catchyTextFieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowCurrent((v) => !v)} edge="end" size="small">
                        {showCurrent ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="New password"
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                fullWidth
                autoComplete="new-password"
                sx={catchyTextFieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowNew((v) => !v)} edge="end" size="small">
                        {showNew ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Confirm new password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                fullWidth
                autoComplete="new-password"
                sx={catchyTextFieldSx}
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: 3,
              display: 'flex',
              justifyContent: { xs: 'stretch', sm: 'flex-end' },
              gap: 1,
            }}
          >
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading}
              fullWidth
              sx={{
                ...catchyPrimaryButtonSx,
                width: { xs: '100%', sm: 'auto' },
                minWidth: 160,
              }}
            >
              {loading ? 'Saving…' : 'Save profile'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default ProfilePage;
